const NO_DECLENSION = "0;,,,";
const NO_DECLENSION_MARKER = "~";

const isChar = (c) => c === "-" || c.toLowerCase() !== c.toUpperCase();
const isNumeric = (c) => /^[0-9]$/.test(c);
const validTerminator = (c) => ["!", "_"].indexOf(c) !== -1;
function deserializeTrie(str) {
    let i = 0;
    const char = () => str.substr(i, 1);
    const next = () => i++;
    function deserializeLeaf() {
        let subtraction = char();
        next(); // Move beyond number to ';' or '~'
        function returnValue(c, value) {
            if (!validTerminator(c))
                throw new Error("INV_TER: " + c);
            return [{ value, children: {} }, c === "!"];
        }
        if (subtraction === NO_DECLENSION_MARKER) {
            const c = char();
            next(); // Move beyond terminator
            return returnValue(c, NO_DECLENSION);
        }
        // Subtraction may be composed of multiple numbers. Keep searching
        {
            let c;
            while (isNumeric((c = char()))) {
                subtraction += c;
                next();
            }
        }
        next(); // Move to first part
        const parts = [];
        for (let i = 0;; i++) {
            let part = "";
            let c = char();
            while (isChar(c)) {
                part += c;
                next();
                c = char();
            }
            // 'c' is terminator or separator
            parts.push(part);
            next(); // Move beyond terminator or separator
            if (i === 3) {
                return returnValue(c, `${subtraction};${parts.join(",")}`);
            }
            // 'c' should be terminator
            if (c !== ",")
                throw new Error("INV_SEP: " + c);
        }
    }
    function deserializeKey() {
        let key = "";
        let c = char();
        while (isChar(c)) {
            key += c;
            next();
            c = char();
        }
        if (c !== ":")
            throw new Error("INV_COL: " + c);
        next(); // Move past ':' to value
        return key;
    }
    function deserializeObject() {
        next(); // Move to first property
        const children = {};
        while (true) {
            const key = deserializeKey();
            const [child, done] = deserialize();
            children[key] = child;
            if (!done)
                continue;
            const c = char(); // Should be terminator
            next(); // Move past terminator
            if (!validTerminator(c))
                throw new Error("INV_TER: " + c);
            return [{ value: "", children }, c === "!"];
        }
    }
    function deserialize() {
        const c = char();
        if (c === NO_DECLENSION_MARKER || isNumeric(c)) {
            return deserializeLeaf();
        }
        if (c === "{") {
            return deserializeObject();
        }
        throw new Error("INV_CHAR: " + c);
    }
    return deserialize()[0];
}

function extractDeclension(trie, name) {
    let node = trie;
    for (const char of name.split("").reverse()) {
        const next = node.children[char];
        if (next) {
            if (next.value) {
                // We've reached a leaf node, return the value
                return next.value;
            }
            // Non-leaf node. Keep traversing the trie
            node = next;
            continue;
        }
        // If there is no next node for the current character, and the
        // current node is not a leaf node, we are either:
        //
        //    1. at a dead end
        //    2. the next node is a "union leaf node"
        //
        // If 'node.children' looks like so:
        //
        //    {
        //      abc: { ... },
        //      d: { ... },
        //    }
        //
        // Then 'abc' is an example of a union leaf node. If 'char' is
        // one of the characters that comprises the key, then the union
        // leaf node contains the value we're looking for.
        //
        for (const key of Object.keys(node.children)) {
            for (let i = 0; i < key.length; i++) {
                if (key.substr(i, 1) === char) {
                    return node.children[key].value;
                }
            }
        }
        // We are at a dead end.
        break;
    }
    return null;
}

var serializedInput = "{e:{t:{t:~_n:0;,,,s!_cghimn:~!_t:{r:{ö:{j:{b:3;ört,örtu,örtu,artar_B:3;ört,ört,ört,artar!!_aeo:0;,,,s!_n:{e:{r:{Aó:0;,,,s_e:0;,,i,s!_Bcv:0;,,,s!_a:0;,,,s!_ó:{j:0;,u,u,ar_Bn:0;,,,ar!_s:{ú:0;,,i,s_en:0;,,,s_iá:0;,,,ar!_e:{ijrí:0;,i,i,ar_b:0;,u,u,ar_f:0;,,i,s!_t:{ió:0;,,,ar_o:0;,,,s!_í:{mr:0;,,,ar_v:0;,i,i,ar!_iou:0;,,,ar_ak:0;,,,s_l:0;,,i,s_é:0;,i,i,ar!_n:{o:{m:{í:{S:0;,,i,ar_T:0;,,i,s!_Aað:0;,,i,s_ó:0;,,,s_r:0;,,i,ar!_l:{ií:0;,,,s_ó:0;,,i,s!_e:{d:0;,,,s_L:0;,,i,s!_s:{a:0;,,i,ar_m:0;,,,ar!_dr:0;,,i,s_t:0;,,,s_k:0;,,i,ar_V:0;,,,ar!_a:{i:{l:{u:0;,,,s_l:~!_rt:0;,,,s!_j:{l:{i:{K:0;,,,s_L:0;,,i,s!!_m:0;,,,s!_v:{Í:0;,,i,s_S:0;,,,s!_t:{a:{n:0;,,,s_N:0;,,i,s!_s:{i:0;,,i,s_o:0;,,,s!_r:0;,,i,s!_DJeklrÍí:0;,,,s_fhm:0;,,i,s_d:0;,,i,ar!_r:{ö:{j:4;jörn,jörn,irni,jörns_H:3;örn,örn,örn,arnar_r:3;örn,örn,erni,arnar!_á:0;,,i,s_Ö:3;Örn,Örn,Erni,Arnar!_n:{i:{l:{a:3;inn,in,ni,ins_r:0;,,,ar!_e:1;n,,i,s_Ff:0;,,,s_rtáð:3;inn,in,ni,ins_gn:1;n,,,s!_u:{ð:{u:1;n,,i,s_Ii:0;,i,i,ar!_t:{ö:3;unn,un,ni,uns_ó:0;,i,i,ar!_glnrsÍæ:0;,i,i,ar!_a:{m:{t:{s:{u:0;,,,s_i:0;,,i,s!_r:0;,,i,s!_r:{euÁ:0;,,i,s_o:0;,,,s!_ð:{iu:0;,,i,s_r:0;,,,s!_glnsæíú:0;,,i,s_i:0;,,,s!_hó:0;,,i,s_ð:1;n,,i,s!_A:~_áú:1;n,,i,s_ö:3;önn,önn,önn,annar!_ú:{r:0;,u,u,ar_R:0;,,,ar!_i:{tvw:0;,,,s_eklr:0;,,,ar!_í:{r:{Aa:0;,,,ar_gt:0;,u,u,ar!_l:{a:{d:{n:0;,,,ar_í:~!_MSjt:0;,,,ar_g:0;,,,s!_k:{n:0;,,,s_s:0;,,,ar!_Hginrstyáðó:0;,,,ar_E:0;,u,u,ar!_m:{a:0;,,,s_s:0;,,,ar!_t:{s:0;,u,u,ar_n:0;,,,s!_LRk:0;,,,ar!_u:{ð:0;,,i,s_gj:0;,,,ar!_e:{r:{aÍ:0;,,,ar_ö:0;,,,s!_t:{s:0;,u,u,ar_r:0;,,,s!_lm:0;,,,ar_bghí:0;,,,s!_f:{ö:3;öfn,öfn,öfn,afnar_eo:0;,,,ar_a:0;,,i,s!_á:{r:{F:0;,,,ar_ð:0;,,i,s!_d:0;,,i,ar_fj:0;,,i,s_R:0;,,,ar!_g:{e:{r:0;,,,ar_R:0;,,i,s!_ö:3;ögn,ögn,ögn,agnar_a:0;,,i,s_Ö:3;Ögn,Ögn,Ögn,Agnar!_ó:0;,,i,s_h:0;,,,s_y:0;,,,jar!_a:{l:{g:{A:4;Agla,Öglu,Öglu,Öglu_EU:1;a,u,u,u!_l:{e:{b:{a:{n:9;Annabella,Önnubellu,Önnubellu,Önnubellu_rs:1;a,u,u,u!!_n:{iníóú:1;a,u,u,u_o:0;,u,u,u!_BHNstí:1;a,u,u,u!_A:4;Alla,Öllu,Öllu,Öllu_EiyÚ:1;a,u,u,u_a:4;alla,öllu,öllu,öllu!_t:{a:4;atla,ötlu,ötlu,ötlu_A:4;Atla,Ötlu,Ötlu,Ötlu_s:1;a,u,u,u!_r:{Eeu:1;a,u,u,u_a:4;arla,örlu,örlu,örlu!_beikosuyÓáæíóú:1;a,u,u,u_a:3;ala,ölu,ölu,ölu!_d:{d:{A:4;Adda,Öddu,Öddu,Öddu_EIOeio:1;a,u,u,u_a:4;adda,öddu,öddu,öddu!_l:{A:4;Alda,Öldu,Öldu,Öldu_iou:1;a,u,u,u_a:4;alda,öldu,öldu,öldu!_n:{a:4;anda,öndu,öndu,öndu_Iiu:1;a,u,u,u!_A:3;Ada,Ödu,Ödu,Ödu_IeiÍÓí:1;a,u,u,u_g:4;agda,ögdu,ögdu,ögdu!_n:{n:{A:4;Anna,Önnu,Önnu,Önnu_IUeiouú:1;a,u,u,u_a:4;anna,önnu,önnu,önnu!_r:{A:4;Arna,Örnu,Örnu,Örnu_EeiÁ:1;a,u,u,u_a:4;arna,örnu,örnu,örnu!_g:{iö:1;a,u,u,u_a:4;agna,ögnu,ögnu,ögnu!_IUefilotÍíðóú:1;a,u,u,u_a:3;ana,önu,önu,önu!_t:{r:{ei:1;a,u,u,u_a:4;arta,örtu,örtu,örtu!_n:{a:4;anta,öntu,öntu,öntu_e:1;a,u,u,u!_a:3;ata,ötu,ötu,ötu_egikstéíóú:1;a,u,u,u_l:4;alta,öltu,öltu,öltu!_h:{t:{r:{e:1;a,u,u,u_a:5;artha,örthu,örthu,örthu!_a:4;atha,öthu,öthu,öthu!!_ð:{r:{euó:1;a,u,u,u_a:4;arða,örðu,örðu,örðu!_g:4;Agða,Ögðu,Ögðu,Ögðu_Iiyíú:1;a,u,u,u!_b:{l:4;Alba,Ölbu,Ölbu,Ölbu_be:1;a,u,u,u!_r:{d:{n:{a:5;andra,öndru,öndru,öndru_A:5;Andra,Öndru,Öndru,Öndru_Ii:1;a,u,u,u!!_t:{ekí:1;a,u,u,u_a:4;atra,ötru,ötru,ötru!_A:3;Ara,Öru,Öru,Öru_efiruáíóý:1;a,u,u,u_a:3;ara,öru,öru,öru_s:~!_f:{l:{A:4;Alfa,Ölfu,Ölfu,Ölfu_EYeiÚ:1;a,u,u,u!_eíóú:1;a,u,u,u_a:3;afa,öfu,öfu,öfu!_s:{í:{l:{AE:1;a,u,u,u_a:8;Annalísa,Önnulísu,Önnulísu,Önnulísu!_DLvú:1;a,u,u,u!_s:{A:4;Assa,Össu,Össu,Össu_i:1;a,u,u,u!_n:{a:4;ansa,önsu,önsu,önsu_e:1;a,u,u,u!_r:{a:4;arsa,örsu,örsu,örsu_Yi:1;a,u,u,u!_eilÁÆÓó:1;a,u,u,u!_m:{l:{A:4;Alma,Ölmu,Ölmu,Ölmu_Eei:1;a,u,u,u_a:~!_r:{a:4;arma,örmu,örmu,örmu_Io:1;a,u,u,u!_m:{Ei:1;a,u,u,u_a:4;amma,ömmu,ömmu,ömmu!_eÍÝíó:1;a,u,u,u!_k:{l:{Eeio:1;a,u,u,u_a:4;alka,ölku,ölku,ölku!_n:{i:1;a,u,u,u_a:4;anka,önku,önku,önku!_eikrsáíó:1;a,u,u,u_a:3;aka,öku,öku,öku!_j:{n:{A:4;Anja,Önju,Önju,Önju_EYioy:1;a,u,u,u_a:4;anja,önju,önju,önju!_k:{s:{A:5;Askja,Öskju,Öskju,Öskju_E:1;a,u,u,u!_k:1;a,u,u,u!_r:{IYi:1;a,u,u,u_a:4;arja,örju,örju,örju!_aglsuyæð:1;a,u,u,u!_v:{l:{EYiö:1;a,u,u,u_a:4;alva,ölvu,ölvu,ölvu!_A:3;Ava,Övu,Övu,Övu_EegikrÍí:1;a,u,u,u_a:3;ava,övu,övu,övu!_g:{g:{eio:1;a,u,u,u_a:4;agga,öggu,öggu,öggu!_ilnou:1;a,u,u,u_a:3;aga,ögu,ögu,ögu!_ú:{D:1;a,u,u,u_s:~!_p:{r:{a:4;arpa,örpu,örpu,örpu_I:1;a,u,u,u!_p:1;a,u,u,u!_ceizÚíó:1;a,u,u,u_y:4;anya,önyu,önyu,önyu!_l:{e:{a:{g:0;,,,ar_fhkmnrs:0;,,,s!_d:{A:0;,,,s_E:0;,,,ar!_t:{r:0;,,,s_s:0;,,,ar!_ó:{B:0;,,,ar_J:0;,,,s!_inrxíú:0;,,,s_bhk:0;,,,ar_v:0;,,i,s!_i:{b:{i:~_á:0;,,,ar!_cms:0;,,,s_d:0;,,,ar!_l:{i:{g:{E:5;Egill,Egil,Agli,Egils_nr:3;ill,il,li,ils!_B:0;,,,s_fkr:3;ill,il,li,ils_t:5;etill,etil,atli,etils_L:0;,,,ar!_eáæ:1;l,,i,s_u:3;ull,ul,li,uls_a:0;,,i,s_ö:3;öll,öll,öll,allar!_mæíó:0;,,,ar_rs:0;,,i,s_a:0;,,,s!_m:{a:{í:{L:0;,,,s_r:0;,,,ar!_i:{r:0;,,,ar_l:0;,,,s!_dhró:0;,,,s_j:0;,,,ar!_eilruí:0;,,,s!_i:{r:{Aadefgimrtáóúý:1;i,a,a,a_y:~!_g:{g:{Uaiouö:1;i,a,a,a_e:1;i,ja,ja,ja!_alnou:1;i,a,a,a!_bdfjklmnpstvxðóú:1;i,a,a,a_u:1;i,ja,ja,ja_a:0;,,,s!_ý:{n:{n:{e:{BHV:0;,,,jar_J:0;,ju,ju,jar!_Aaiou:0;,,,jar_áó:0;,ju,ju,jar!_ó:{n:0;,,,s_J:0;,,,jar!_g:{a:{D:0;,ju,ju,jar_MR:0;,,,jar!_ir:0;,ju,ju,jar!_dfklrstæéð:0;,ju,ju,jar_o:0;,,,s!_v:{en:0;,,,s_l:0;,,,jar!_r:{r:{iu:0;,,,jar_a:0;,,,s!_n:0;,,,s_au:0;,,,jar!_dglmst:0;,,,jar!_f:{l:{ó:{d:{A:0;,,,s_ú:0;,,i,s!_ð:0;,,,s!_o:0;,,,s!_i:{e:{l:0;,,,ar_L:0;,,,s!_Ss:0;,,,jar!_í:0;,,,ar_aeu:0;,,,s_ú:0;,u,u,ar_ö:2;öf,öfu,öfu,afar!_g:{r:{e:{b:{n:{aeinrí:0;,,,s_u:0;,,i,s!_s:{Áíó:0;,,,s_s:0;,,i,s!_d:{l:0;,,i,s_n:0;,,,s!_m:{l:0;,,,s_u:0;,,i,s!_g:{n:{I:0;,,,s_y:0;,,i,s!_u:0;,,,s!_æ:{n:0;,,i,s_S:0;,,,s!_Efilrtyð:0;,,,s_ó:0;,,i,s!_B:0;,,,s!_o:{b:{dgilnrstyæð:0;,u,u,ar_ef:0;,,,ar!_B:0;,,,ar_e:0;,,,s!_ö:3;örg,örgu,örgu,argar_a:0;,,i,s!_i:{e:0;,u,u,ar_v:0;,,,s!_n:{i:{mnr:0;,,,s_l:0;,,i,s!!_g:{ö:3;ögg,ögg,ögg,aggar_i:0;,,,jar!_u:0;,u,u,ar_í:0;,,,s!_r:{u:{g:{n:{iæ:2;ur,,i,s_e:2;ur,,,s!_g:{euy:2;ur,,i,s_ö:2;ur,,,s!_i:{e:2;ur,,i,s_V:0;,,,s!_ruy:2;ur,,i,s_a:4;agur,ag,egi,ags_í:2;ur,,,s!_ð:{i:{v:{lnty:2;ur,,i,ar_g:2;ur,,i,s!_e:2;ur,i,i,ar_Em:2;ur,,i,s_r:2;ur,,i,ar!_r:{a:{v:{ð:{u:2;ur,,i,s_áóö:2;ur,,i,ar!_lnrstá:2;ur,,i,ar_g:2;ur,,i,s!_bghkr:2;ur,,i,s!_ö:{HMV:5;örður,örð,erði,arðar_j:6;jörður,jörð,irði,jarðar!_Ue:2;ur,i,i,ar_uáó:2;ur,,i,ar!_ö:{r:2;ur,,i,ar_l:2;ur,,,s_H:4;öður,öð,eði,aðar!_e:{r:{f:{gn:2;ur,,i,s_lð:2;ur,,i,ar!!!_u:{a:2;ur,i,i,ar_f:7;örfuður,örfuð,örfuði,arfaðar!_a:{k:2;ur,,i,ar_l:2;ur,,i,s!_íú:2;ur,i,i,ar_áó:2;ur,,i,s_ý:2;ur,,,s!_d:{n:{i:{v:2;ur,,i,ar_Tl:2;ur,,i,s!_uá:2;ur,,i,ar_ae:2;ur,,i,s!_l:{a:{n:{r:2;ur,,i,ar_ó:2;ur,,i,s!_v:{t:{s:{Á:2;ur,,i,ar_i:2;ur,,i,s!!_s:{Á:2;ur,,i,ar_Ó:2;ur,,i,s!_r:{i:2;ur,,i,s_o:2;ur,,i,ar!_giknæ:2;ur,,i,s!_BGb:2;ur,ur,ri,urs_jmró:2;ur,,i,s!_ei:2;ur,i,i,ar_E:2;ur,,i,s_u:2;ur,,i,ar_ö:6;jöldur,jöld,ildi,jaldar!_d:2;ur,,i,s!_k:{a:{lþ:2;ur,,i,s_V:2;ur,ur,ri,urs!_eisuáí:2;ur,,i,s_r:5;örkur,örk,erki,arkar!_n:{n:{i:2;ur,,i,s_Uu:2;ur,i,i,ar!_ay:2;ur,,i,s!_m:{l:{áó:2;ur,,i,s_I:2;ur,i,i,ar!_iryíóú:2;ur,,i,s!_f:{l:{ÁÚóú:2;ur,,i,s_EYe:2;ur,i,i,ar!_í:{l:2;ur,,i,s_v:2;ur,ur,ri,urs!_aióú:2;ur,,i,s!_t:{r:{a:2;ur,,i,s_ö:6;jörtur,jört,irti,jartar!_s:{ae:2;ur,,i,s_ö:5;östur,öst,esti,astar!_t:{e:2;ur,,i,s_ö:5;öttur,ött,etti,attar_ó:2;ur,,i,ar!_afiuóú:2;ur,,i,s_Oeé:2;ur,ur,ri,urs!_h:0;,,i,s_lp:2;ur,,i,s_sz:0;,,i,ar!_a:{m:{n:{n:0;,,,s_iy:0;,,i,s!_t:{s:{eiu:0;,,i,s_Á:0;,,,s!_r:0;,,i,s!_s:{Á:0;,,,s_Í:0;,,i,s!_g:{r:0;,,,s_a:0;,,,ar_in:0;,,i,s!_i:{elr:0;,,i,s_dg:0;,,,s!_í:{l:0;,,,s_r:0;,,i,s!_ð:{i:{r:0;,,,s_e:0;,,i,s!_uóú:0;,,i,s!_a:{H:0;,,i,s_T:0;,,,ar!_dlryÓó:0;,,i,s_e:0;,,,s!_s:{eÁÍæó:0;,,i,s_a:0;,,,s!_dfghjklnprtvxðó:0;,,i,s_M:0;,,,s!_i:{e:{g:0;,,i,s_G:0;,,,s!_dgklmnrstvð:1;r,,,s!_e:{h:{t:{s:0;,,,ar_ú:0;,,,s!_p:0;,,,s!_t:{s:{E:0;,,,ar_i:0;,,,s!_x:0;,,i,s_elú:0;,,,s!_g:{gl:0;,,,s_n:0;,,,ar!_PVdfnpsvz:0;,,,s_b:~!_o:{ghmní:0;,,,s_t:0;,,i,s!_r:{a:1;r,,i,s_ö:3;örr,örr,erri,arrar_y:1;r,,,s_Ý:1;r,i,i,ar!_ó:{dnþ:0;,,i,s_DThÞ:0;,,,s!_y:{e:{rÞ:1;r,,,s_R:0;,,,s!_t:0;,,,s!_ú:{ht:0;,,i,s_k:0;,,,s!_á:{Mr:1;r,,,s_k:0;,,i,s_KL:0;,,,s_V:0;,,,ar!_í:0;,,,s_æéý:1;r,,,s_ö:2;ör,öru,öru,arar_d:1;r,,i,s_ÍÝ:0;,i,i,ar!_s:{í:{l:{A:0;,i,i,ar_l:0;,,i,ar!_r:{A:0;,i,i,ar_a:0;,,,ar!_d:0;,i,i,ar_D:0;,,,ar!_e:{n:{r:{A:0;,,i,ar_a:0;,,i,s!_g:0;,i,i,ar_An:0;,,i,ar_f:0;,,i,s!_r:{d:0;,,i,ar_A:0;,,,ar!_l:{r:{a:{h:~_K:0;,,i,ar!!!_d:{e:0;,,i,s_í:0;,i,i,ar!_m:{a:~_r:0;,,i,!_s:{ó:~_m:0;,,i,ar!_J:~_tv:0;,,i,ar!_u:{m:{e:0;,,i,s_s:0;,,i,ar!_n:{aíú:0;,,i,ar_e:0;,,,ar!_defghprí:0;,,i,ar!_n:{e:{Jmr:~_t:0;,,i,!_o:0;,,,ar_a:~!_a:{l:{t:0;,,i,ar_o:0;,,i,!_m:{o:~_uó:0;,,i,ar!_ejkníóý:0;,,i,ar_t:0;,,,ar!_i:{r:{Ao:0;,,,ar_Í:0;,i,i,ar!_n:~_ls:0;,,,ar_v:0;,,i,s!_l:{i:{g:0;,,i,_GN:~!_eí:~!_s:{Á:~_o:0;,,i,!_á:{l:{u:0;,,i,ar_r:0;,,i,s!!_ó:0;,,,ar_oéú:0;,,i,ar_rt:~_Á:0;,,,s!_y:{e:{m:{girtð:0;,,,jar_lmuíó:0;,ju,ju,jar!_l:{ntx:0;,,,s_alrsáó:0;,ju,ju,jar_e:0;,,,jar!_g:{n:{I:0;,ju,ju,jar_i:0;,,,jar!_goru:0;,ju,ju,jar_l:0;,,,jar!_ð:{i:{r:0;,ju,ju,jar_V:0;,,,jar!_a:0;,ju,ju,jar!_Mdfknrstvó:0;,ju,ju,jar!_n:{o:0;,,,s_n:0;,,,jar!_r:{nr:0;,,,s_a:0;,,,jar!_l:{l:{o:0;,,,jar_i:0;,,,s!!_E:0;,,,jar!_d:{l:{a:{n:{r:0;,,i,ar_oó:0;,,,s_y:0;,,i,s!_v:{t:0;,,i,ar_nsæð:0;,,i,s_E:0;,,,s!_r:{e:0;,,,s_a:0;,,i,s!_m:0;,,,s_b:0;,,i,s!_o:{n:0;,,i,s_fs:0;,,,ar_hp:0;,,,s!_u:0;,,,ar_i:0;,i,i,ar!_n:{a:{l:0;,,i,s_n:0;,,,s!_iu:0;,,,ar_e:0;,,,s!_r:{a:{h:{n:{r:0;,,i,s_io:0;,,,s!_c:0;,,,s!_lnvw:0;,,,s_g:0;,,,ar!!_e:0;,,,s_im:0;,,,ar_d:3;ödd,ödd,ödd,addar!_ð:{r:{a:{ghv:0;,,i,s_n:0;,,,s!!_í:{v:0;,,,s_r:0;,i,i,ar!_á:{r:{l:0;,i,i,ar_n:0;,,i,s!_N:0;,,,ar!_e:0;,,,s_ö:2;öð,öð,öð,aðar_ó:0;,,,ar!_k:{r:{ö:3;örk,örk,örk,arkar_a:0;,,i,s_Ö:3;Örk,Örk,Örk,Arkar!_i:{r:{dlnð:0;,,i,s_Etú:0;,,,s!_n:0;,,,s_E:0;,,,ar_e:0;,,,ur!_e:{r:{bú:0;,,,s_t:0;,,i,s!!_í:{v:{ð:{ú:0;,,,s_i:0;,,,ur!_y:0;,,,ur!!_ö:2;ök,öku,öku,akar_sá:0;,,,ar_acno:0;,,,s_l:0;,,i,s_k:3;ökk,ökk,ökk,akkar!_í:{n:{ó:0;,,,s_í:0;,,,ar!_m:{a:0;,,,ar_oó:~!_bdrs:0;,,,ar_lv:0;,,,s!_ó:{g:{ó:0;,,,ar_gú:0;,,,s!_r:{a:0;,,,ar_e:0;,,,s!_k:{i:0;,,,ar_r:0;,,,s!_entí:0;,,,s_l:0;,,,ar!_h:{t:0;,,,ar_c:0;,,,s!_p:{eio:0;,,,s_s:3;Ösp,Ösp,Ösp,Aspar!_v:{i:{L:0;,,,ar_S:0;,,,jar!_a:0;,,,s_í:0;,,,ar!_xz:~_á:0;,,,r_bcjoé:0;,,,s!!";

// We can't hard-code this to something like "@@input@@" because
// rollup considers that to be dead code and eliminates it.
//
// Anyway, this will be replaced with a constant in builds.
var mode = "names"; // Set to either 'names' or 'addresses' in builds

const trie = deserializeTrie(serializedInput);
const casesIs = ["nf", "þf", "þgf", "ef"];
const casesEn = ["nom", "acc", "dat", "gen"];
function getCaseIndex(caseStr) {
    const indexIs = casesIs.indexOf(caseStr);
    if (indexIs !== -1)
        return indexIs;
    const indexEn = casesEn.indexOf(caseStr);
    if (indexEn !== -1)
        return indexEn;
    return 0; // Fall back to 0 if an invalid case was provided
}
function parseDeclension(declension) {
    const [subtractString, appendicesString] = declension.split(";");
    const subtraction = Number(subtractString);
    const appendices = (appendicesString || "").split(",");
    return [subtraction, appendices];
}
function declineName(name, declension, caseStr) {
    const [subtraction, appendices] = parseDeclension(declension);
    const caseIndex = getCaseIndex(caseStr);
    // Should not happen, but prefer being safe
    if (!Number.isFinite(subtraction))
        return name;
    if (appendices.length !== 4)
        return name;
    const root = name.substr(0, name.length - subtraction);
    return root + appendices[caseIndex];
}
let predicate;
function setPredicate(pred) {
    predicate = pred;
}
function applyCaseToName(caseStr, name) {
    if (mode === "names") {
        const namesThatEndWithSon = ["Samson", "Jason"];
        const endsWithSon = namesThatEndWithSon.indexOf(name) !== -1;
        if (!endsWithSon) {
            for (const [ending, declension] of [
                ["son", "2;on,on,yni,onar"],
                ["dóttir", "2;ir,ur,ur,ur"],
                ["bur", "0;,,i,s"],
            ]) {
                if (!name.endsWith(ending))
                    continue;
                name = name.split(ending)[0];
                return name + declineName(ending, declension, caseStr);
            }
        }
    }
    const declension = getDeclensionForName(name);
    if (declension && (!predicate || predicate(name)))
        name = declineName(name, declension, caseStr);
    return name;
}
/**
 * Applies a case to a source name provided in the nominative
 * case (nefnifall).
 *
 * @example
 * ```tsx
 * applyCase("ef", "Jóhann");
 * //=> "Jóhannesar"
 *
 * applyCase("þgf", "Helga Fríða Smáradóttir");
 * //=> "Helgu Fríðu Smáradóttur"
 * ```
 *
 * The name provided must be an Icelandic name in the nominative case
 * (nefnifall). Otherwise, an unexpected output is likely.
 *
 * The supported cases are:
 *
 * - Nominative `nom` (nefnifall `nf` in Icelandic)
 * - Accusative `acc` (þolfall `þf` in Icelandic)
 * - Dative `dat` (þágufall `þgf` in Icelandic)
 * - Genitive `gen` (eignarfall `ef` in Icelandic)
 *
 * Note that superfluous whitespace is not retained:
 *
 * @example
 * ```tsx
 * applyCase("þf", "  \n  Hildigerður  Oddný\tPatreksdóttir  \n\n");
 * //=> "Hildigerði Oddnýju Patreksdóttur"
 * ```
 *
 * @param name - An Icelandic name in the nominative case (nefnifall)
 * @param caseStr - The case to apply to the name to, e.g. `þf`
 */
function applyCase(caseStr, name) {
    return name
        .split(/\s+/)
        .filter(Boolean)
        .map((name) => {
        if (mode === "names")
            return applyCaseToName(caseStr, name);
        return name
            .split("-")
            .map((name) => applyCaseToName(caseStr, name))
            .join("-");
    })
        .join(" ");
}
function getDeclensionForName(name) {
    if (name.split(/\s+/).length > 1)
        throw new Error("Name must not include whitespace");
    const declension = extractDeclension(trie, name);
    if (declension) {
        // Ensure that the declension applies to the name. This guard is useful when
        // the subtraction is longer than the path to the declension.
        //
        // For example, consider this:
        //
        //    {
        //      "path": "ya",
        //      "children": {},
        //      "keys": ["Tanya"],
        //      "value": "4;anya,önyu,önyu,önyu"
        //    }
        //
        // The name 'Maya' matches this path, but applying the declension erases the
        // entire name.
        const [_subtraction, appendices] = parseDeclension(declension);
        if (!name.endsWith(appendices[0]))
            return null;
    }
    return declension;
}

export { applyCase, getDeclensionForName, setPredicate };
